import React, { useState, useEffect, ReactNode } from "react";
import LoadingScreen from "./LoadingScreen";
import { AuthContext } from "../contexts/AuthContext";
import { User } from "../types/user";

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for stored user on component mount
  useEffect(() => {
    const checkStoredUser = () => {
      try {
        console.log("AuthProvider: Checking stored user...");
        const storedUser = localStorage.getItem("user");
        const storedToken = localStorage.getItem("token");

        console.log(
          "AuthProvider: Stored user:",
          storedUser ? "Found" : "Not found"
        );
        console.log(
          "AuthProvider: Stored token:",
          storedToken ? "Found" : "Not found"
        );

        if (storedUser && storedToken) {
          const userData = JSON.parse(storedUser);
          const tokenData = JSON.parse(storedToken);

          console.log("AuthProvider: Parsed user data:", userData);
          console.log(
            "AuthProvider: Parsed token data:",
            tokenData ? "Valid" : "Invalid"
          );

          if (tokenData && userData) {
            // Create a User object that matches the FirebaseUser interface
            const formattedUser: User = {
              uid: userData.userId || userData.uid || "",
              email: userData.email || "",
              displayName:
                userData.name ||
                userData.firstName + " " + userData.lastName ||
                "",
              emailVerified: userData.emailVerified || false,
              isAnonymous: false,
              metadata: {
                creationTime: userData.createdAt || new Date().toISOString(),
                lastSignInTime: new Date().toISOString(),
              },
              providerData: [],
              refreshToken: tokenData.refreshToken || "",
              tenantId: null,
              phoneNumber: null,
              photoURL: null,
              providerId: "aws-cognito",
              role: (userData.role || "user") as
                | "admin"
                | "user"
                | "editor"
                | "viewer",
              delete: async () => {},
              getIdToken: async () => tokenData.idToken || "",
              getIdTokenResult: async () =>
                ({ token: tokenData.idToken || "", claims: {} } as any),
              reload: async () => {},
              toJSON: () => ({}),
            };

            setUser(formattedUser);
            setIsAdmin(
              userData.email === "admin@queryverse.ai" ||
                (userData.email && userData.email.endsWith("@admin.com")) ||
                false
            );
            setIsLoading(false);
            console.log(
              "AuthProvider: User restored successfully:",
              formattedUser.email
            );
            return true;
          }
        }
      } catch (error) {
        console.error(
          "AuthProvider: Failed to restore user from storage:",
          error
        );
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      }

      setIsLoading(false);
      console.log("AuthProvider: No valid user data found");
      return false;
    };

    checkStoredUser();

    // Listen for storage changes (when user logs in from AuthForm)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "user" || e.key === "token") {
        checkStoredUser();
      }
    };

    // Listen for custom login event
    const handleUserLoggedIn = () => {
      setTimeout(() => {
        checkStoredUser();
      }, 100);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("userLoggedIn", handleUserLoggedIn);

    // Debug: Monitor localStorage changes
    // const originalSetItem = localStorage.setItem;
    // const originalRemoveItem = localStorage.removeItem;
    // const originalClear = localStorage.clear;

    // localStorage.setItem = function (key, value) {
    //   console.log("localStorage.setItem:", key, value);
    //   return originalSetItem.call(this, key, value);
    // };

    // localStorage.removeItem = function (key) {
    //   console.log("localStorage.removeItem:", key);
    //   // Prevent accidental clearing of auth data during navigation
    //   if (
    //     (key === "user" || key === "token") &&
    //     window.location.pathname !== "/login"
    //   ) {
    //     console.warn(
    //       "Prevented localStorage removal of",
    //       key,
    //       "during navigation"
    //     );
    //     return;
    //   }
    //   return originalRemoveItem.call(this, key);
    // };

    // localStorage.clear = function () {
    //   console.log("localStorage.clear called");
    //   return originalClear.call(this);
    // };

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userLoggedIn", handleUserLoggedIn);
      // Restore original localStorage methods
      // localStorage.setItem = originalSetItem;
      // localStorage.removeItem = originalRemoveItem;
      // localStorage.clear = originalClear;
    };
  }, []);

  // Add loading timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn("Auth loading timeout reached, stopping loading state");
        setIsLoading(false);
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  const login = () => {};

  const logout = async () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    setIsAdmin(false);
  };

  const refreshUser = () => {
    try {
      const storedUser = localStorage.getItem("user");
      const storedToken = localStorage.getItem("token");

      if (storedUser && storedToken) {
        const userData = JSON.parse(storedUser);
        const tokenData = JSON.parse(storedToken);

        if (tokenData && userData) {
          // Create a User object that matches the FirebaseUser interface
          const formattedUser: User = {
            uid: userData.userId || userData.uid || "",
            email: userData.email || "",
            displayName:
              userData.name ||
              userData.firstName + " " + userData.lastName ||
              "",
            emailVerified: userData.emailVerified || false,
            isAnonymous: false,
            metadata: {
              creationTime: userData.createdAt || new Date().toISOString(),
              lastSignInTime: new Date().toISOString(),
            },
            providerData: [],
            refreshToken: tokenData.refreshToken || "",
            tenantId: null,
            phoneNumber: null,
            photoURL: null,
            providerId: "aws-cognito",
            role: (userData.role || "user") as
              | "admin"
              | "user"
              | "editor"
              | "viewer",
            delete: async () => {},
            getIdToken: async () => tokenData.idToken || "",
            getIdTokenResult: async () =>
              ({ token: tokenData.idToken || "", claims: {} } as any),
            reload: async () => {},
            toJSON: () => ({}),
          };

          setUser(formattedUser);
          setIsAdmin(
            userData.email === "admin@queryverse.ai" ||
              (userData.email && userData.email.endsWith("@admin.com")) ||
              false
          );
          // Dispatch custom event to trigger navigation
          window.dispatchEvent(new CustomEvent("userLoggedIn"));
        }
      }
    } catch (error) {
      console.error("Error in refreshUser:", error);
    }
  };

  if (isLoading) {
    return React.createElement(LoadingScreen);
  }

  return React.createElement(
    AuthContext.Provider,
    { value: { user, isAdmin, isLoading, login, logout, refreshUser } },
    children
  );
};

export default AuthProvider;
